import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
  Tag,
} from '@jem-open/jem-ui';
import {
  getPendingTransactions,
  getSubmissionTransactions,
} from './services/savingsBatchService';
import { statusVariant } from './transactionStatus';
import type { ProviderPaymentSubmission, SubmissionTransaction } from './types';

// `pending` drills into the outstanding pool; a submission drills into the
// lines that made up that payment.
export type BreakdownTarget = ProviderPaymentSubmission | 'pending';

interface SubmissionBreakdownModalProps {
  target: BreakdownTarget | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const formatCurrency = (amount: string): string => {
  const n = Number(amount);
  if (!Number.isFinite(n)) return amount;
  return `R${n.toLocaleString('en-ZA', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

const SubmissionBreakdownModal = ({
  target,
  open,
  onOpenChange,
}: SubmissionBreakdownModalProps) => {
  const [transactions, setTransactions] = useState<SubmissionTransaction[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  const isPending = target === 'pending';

  useEffect(() => {
    // Return undefined (not a bare `return;`) so this effect consistently
    // returns a value — matching the cleanup below (consistent-return).
    if (!open || !target) return undefined;
    let cancelled = false;
    // Clear stale lines/error up front so a refetch never shows the previous
    // target's totals in the header, and a failure can't masquerade as "empty".
    setTransactions([]);
    setLoadError(null);
    setLoading(true);
    const fetch = isPending
      ? getPendingTransactions()
      : getSubmissionTransactions((target as ProviderPaymentSubmission).id);
    fetch
      .then((data) => {
        if (!cancelled) setTransactions(data);
      })
      .catch(() => {
        if (!cancelled) {
          setTransactions([]);
          setLoadError('Failed to load transactions. Please try again.');
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, target, isPending]);

  const title = isPending
    ? 'Outstanding (ready to pay)'
    : (target as ProviderPaymentSubmission)?.transactionReference ?? 'Payment';

  // The backend `payable` flag is the source of truth (status gates first,
  // then Franc-account validity for PENDING) — keep the headline figure equal
  // to what was (or would be) sent to the provider, matching the Outstanding
  // row and the submission record exactly (ENG-4670).
  const payableTotal = transactions
    .filter((t) => t.payable)
    .reduce((sum, t) => sum + (Number(t.amount) || 0), 0);
  const excluded = transactions.filter((t) => !t.payable);
  const excludedTotal = excluded.reduce(
    (sum, t) => sum + (Number(t.amount) || 0),
    0
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-3xl">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>
            {transactions.length - excluded.length} payable ·{' '}
            {formatCurrency(payableTotal.toFixed(2))}
            {excluded.length > 0 &&
              ` · ${excluded.length} excluded (${formatCurrency(
                excludedTotal.toFixed(2)
              )})`}
          </DialogDescription>
        </DialogHeader>

        <div className="max-h-[60vh] overflow-y-auto">
          {loading ? (
            <div className="space-y-2">
              {[0, 1, 2, 3, 4].map((i) => (
                <Skeleton key={i} className="h-10 w-full" />
              ))}
            </div>
          ) : loadError ? (
            <p className="text-sm text-red-600">{loadError}</p>
          ) : transactions.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              No transactions to show.
            </p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Employee</TableHead>
                  <TableHead>Employer</TableHead>
                  <TableHead className="text-right">Batch</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Amount</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {transactions.map((txn) => (
                  <TableRow key={txn.id}>
                    <TableCell className="font-medium">
                      {txn.employeeName}
                    </TableCell>
                    <TableCell>{txn.employerName}</TableCell>
                    <TableCell className="text-right">#{txn.batchId}</TableCell>
                    <TableCell>
                      <Tag variant={statusVariant(txn.status)}>
                        {txn.status}
                      </Tag>
                    </TableCell>
                    <TableCell className="text-right">
                      {formatCurrency(txn.amount)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
              <TableFooter>
                <TableRow>
                  <TableCell colSpan={4} className="font-semibold">
                    Payable ({transactions.length - excluded.length})
                  </TableCell>
                  <TableCell className="text-right font-semibold">
                    {formatCurrency(payableTotal.toFixed(2))}
                  </TableCell>
                </TableRow>
                {excluded.length > 0 && (
                  <TableRow>
                    <TableCell
                      colSpan={4}
                      className="font-semibold text-greyscale-text-caption">
                      Excluded ({excluded.length})
                    </TableCell>
                    <TableCell className="text-right font-semibold text-greyscale-text-caption">
                      {formatCurrency(excludedTotal.toFixed(2))}
                    </TableCell>
                  </TableRow>
                )}
              </TableFooter>
            </Table>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default SubmissionBreakdownModal;
